/**
 * gaussian_splat_viewer.js
 *
 * Loads and renders a Gaussian Splat (.splat binary) inside the TwinWorld
 * Cesium scene.  Uses a floating <canvas> overlay anchored to the Cesium
 * container — the standard web approach for gsplat.js, which draws to a
 * WebGL2 canvas independently of Cesium.
 *
 * Dependencies:
 *   - gsplat  (npm: gsplat)  — or the CDN bundle loaded below as a fallback.
 *   - window.viewer           — Cesium Viewer, must be ready.
 *   - getBaseUrl()            — from envUtils.
 *
 * Public API (all exposed on `window`):
 *   window.loadGaussianSplat(jobId, options?)
 *   window.removeGaussianSplat()
 *   window.isGaussianSplatActive() → boolean
 */

import { getBaseUrl } from '../../envUtils';

// ── gsplat.js dynamic loader ──────────────────────────────────────────────────
// We lazy-load the gsplat bundle from CDN so the main bundle stays small.
const GSPLAT_CDN = 'https://cdn.jsdelivr.net/npm/gsplat@latest/build/index.min.js';

let _gsplatModule = null;

const loadGsplatLib = () => {
    if (_gsplatModule) return Promise.resolve(_gsplatModule);

    return new Promise((resolve, reject) => {
        // Try native ES-module import first (works in modern bundlers)
        import('gsplat')
            .then((mod) => {
                _gsplatModule = mod;
                resolve(mod);
            })
            .catch(() => {
                // Fallback: inject CDN <script> tag
                if (window.GaussianSplat) {
                    _gsplatModule = window.GaussianSplat;
                    resolve(_gsplatModule);
                    return;
                }
                const s = document.createElement('script');
                s.src  = GSPLAT_CDN;
                s.type = 'module';
                s.onload = () => {
                    _gsplatModule = window.GaussianSplat || {};
                    resolve(_gsplatModule);
                };
                s.onerror = () => reject(new Error('Failed to load gsplat.js from CDN'));
                document.head.appendChild(s);
            });
    });
};

// ── State ─────────────────────────────────────────────────────────────────────
// Support multiple splats active at once, keyed by jobId
const _activeSplats = {};  // { [jobId]: { canvas, renderer, animFrame } }

// ── Overlay canvas helpers ────────────────────────────────────────────────────

/**
 * Create a unique transparent canvas overlay on top of the Cesium canvas
 * for each splat job. Returns the new canvas element.
 */
const createOverlayCanvas = (jobId) => {
    const cesiumCanvas = document.querySelector('twinworld-app')
        ?.shadowRoot
        ?.querySelector('#cesiumContainer canvas');

    if (!cesiumCanvas) throw new Error('Cesium canvas not found');

    const canvas = document.createElement('canvas');
    canvas.id     = `gaussian-splat-overlay-${jobId}`;
    canvas.width  = cesiumCanvas.width;
    canvas.height = cesiumCanvas.height;
    canvas.style.cssText = `
        position: absolute;
        top: 0; left: 0;
        pointer-events: none;
        z-index: 10;
    `;

    cesiumCanvas.parentElement.style.position = 'relative';
    cesiumCanvas.parentElement.appendChild(canvas);

    // Resize overlay when Cesium canvas resizes
    const ro = new ResizeObserver(() => {
        if (canvas.isConnected) {
            canvas.width  = cesiumCanvas.width;
            canvas.height = cesiumCanvas.height;
        }
    });
    ro.observe(cesiumCanvas);

    return canvas;
};

const removeOverlayCanvas = (jobId) => {
    const canvas = document.getElementById(`gaussian-splat-overlay-${jobId}`);
    if (canvas) canvas.remove();
};

// ── Coordinate conversion ─────────────────────────────────────────────────────

/**
 * Convert WGS-84 lat/lon/height → gsplat.js world-space position.
 *
 * gsplat.js uses a simple THREE.js-style coordinate system. We translate
 * Cesium ECEF to a local East-North-Up frame centred on the scene origin,
 * then scale to metres.
 *
 * @param {number} lat   Latitude  (degrees)
 * @param {number} lon   Longitude (degrees)
 * @param {number} alt   Altitude  (metres, default = scene sea-level height)
 * @returns {{ x: number, y: number, z: number }}
 */
const cesiumCoordsToSplatSpace = (lat, lon, alt) => {
    const origin = window.destSelectedCenter;
    if (!origin) return { x: 0, y: 0, z: 0 };

    const originCart = Cesium.Cartesian3.fromDegrees(
        (origin.longitude * 180) / Math.PI,
        (origin.latitude  * 180) / Math.PI,
        origin.height || 0
    );
    const targetCart = Cesium.Cartesian3.fromDegrees(lon, lat, alt || origin.height || 0);
    const diff = Cesium.Cartesian3.subtract(targetCart, originCart, new Cesium.Cartesian3());

    // Transform ECEF diff → local ENU
    const transform = Cesium.Transforms.eastNorthUpToFixedFrame(originCart);
    const invTransform = Cesium.Matrix4.inverseTransformation(transform, new Cesium.Matrix4());
    const local = Cesium.Matrix4.multiplyByPoint(invTransform, diff, new Cesium.Cartesian3());

    return { x: local.x, y: local.z, z: -local.y }; // Y-up for gsplat
};

// ── Main render loop sync ─────────────────────────────────────────────────────

/**
 * Synchronise the gsplat camera with the Cesium camera every frame.
 * This is what makes the splat appear "pinned" to the correct world location.
 */
const syncCameraAndRender = (renderer, camera) => {
    const cesiumCam = window.viewer?.camera;
    if (!cesiumCam || !renderer || !camera) return;

    // Get Cesium camera position in local ENU
    const origin = window.destSelectedCenter;
    if (!origin) return;

    const originCart = Cesium.Cartesian3.fromDegrees(
        (origin.longitude * 180) / Math.PI,
        (origin.latitude  * 180) / Math.PI,
        origin.height || 0
    );
    const transform = Cesium.Transforms.eastNorthUpToFixedFrame(originCart);
    const invTransform = Cesium.Matrix4.inverseTransformation(transform, new Cesium.Matrix4());

    const camPosLocal = Cesium.Matrix4.multiplyByPoint(
        invTransform,
        cesiumCam.position,
        new Cesium.Cartesian3()
    );

    // Set gsplat camera position (Y-up)
    camera.position.x = camPosLocal.x;
    camera.position.y = camPosLocal.z;
    camera.position.z = -camPosLocal.y;

    // Sync field-of-view from Cesium frustum
    const frustum = cesiumCam.frustum;
    if (frustum.fovy) {
        camera.fov = Cesium.Math.toDegrees(frustum.fovy);
    }

    renderer.render();
};

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Load and display a Gaussian Splat result from the clash-gaussian-splatting
 * service into the active TwinWorld Cesium scene.
 * Multiple splats can be active at once, each has its own canvas overlay.
 *
 * @param {string} jobId       - The job_id returned by POST /process-images
 * @param {object} [options]
 * @param {number} [options.lat]         - World latitude to anchor the splat (degrees)
 * @param {number} [options.lon]         - World longitude
 * @param {number} [options.alt]         - Altitude (metres)
 * @param {number} [options.scale]       - Uniform scale factor (default 1.0)
 * @param {string} [options.accessToken] - Bearer token; falls back to cookie
 */
window.loadGaussianSplat = async (jobId, options = {}) => {
    console.log('🔵 [GaussianSplat] Loading splat for job:', jobId);

    // Remove existing instance for this jobId if any
    window.removeGaussianSplat(jobId);

    try {
        const gsplat = await loadGsplatLib();
        const splatUrl = `${getBaseUrl()}/gaussian-splatting/splat/${jobId}`;
        console.log('🔵 [GaussianSplat] Fetching from:', splatUrl);

        const canvas = createOverlayCanvas(jobId);
        const { Renderer, Scene, Camera, Loader } = gsplat;

        const renderer = new Renderer(canvas, { antialias: true, alpha: true });
        const scene    = new Scene();
        const camera   = new Camera();

        // Fetch .splat binary with auth
        const { default: Cookies } = await import('js-cookie');
        const { parseTwinupCookie, getCookie, formatBearerToken } = await import('./auth_utils');
        const cookieData  = parseTwinupCookie(Cookies.get(getCookie()));
        const accessToken = options.accessToken || cookieData.access_token;

        const res = await fetch(splatUrl, {
            credentials: 'include',
            headers: accessToken ? { Authorization: formatBearerToken(accessToken) } : {},
        });

        if (!res.ok) throw new Error(`Failed to fetch splat file: ${res.status} ${res.statusText}`);
        const buffer = await res.arrayBuffer();
        await Loader.LoadFromArrayBuffer(buffer, scene, renderer);

        // Anchor in world-space
        if (options.lat !== undefined && options.lon !== undefined) {
            const worldPos = cesiumCoordsToSplatSpace(options.lat, options.lon, options.alt);
            scene.position.x = worldPos.x;
            scene.position.y = worldPos.y;
            scene.position.z = worldPos.z;
        }
        if (options.scale) {
            scene.scale.x = scene.scale.y = scene.scale.z = options.scale;
        }

        // Per-splat render loop synced to Cesium camera
        const tick = () => {
            if (!_activeSplats[jobId]) return;
            syncCameraAndRender(renderer, camera);
            _activeSplats[jobId].animFrame = requestAnimationFrame(tick);
        };

        _activeSplats[jobId] = { canvas, renderer, scene, animFrame: null };
        _activeSplats[jobId].animFrame = requestAnimationFrame(tick);

        console.log('✅ [GaussianSplat] Splat loaded and rendering, job:', jobId);
        return true;

    } catch (err) {
        console.error('❌ [GaussianSplat] Failed to load splat:', err);
        window.removeGaussianSplat(jobId);
        return false;
    }
};

/**
 * Stop rendering and remove a specific Gaussian Splat from the scene.
 * If jobId is omitted, all active splats are removed.
 */
window.removeGaussianSplat = (jobId) => {
    if (!jobId) {
        // Remove all
        Object.keys(_activeSplats).forEach((id) => window.removeGaussianSplat(id));
        return;
    }
    const splat = _activeSplats[jobId];
    if (!splat) return;
    if (splat.animFrame) cancelAnimationFrame(splat.animFrame);
    try { splat.renderer?.dispose?.(); } catch (_) {}
    removeOverlayCanvas(jobId);
    delete _activeSplats[jobId];
    console.log('🗑️  [GaussianSplat] Splat removed, job:', jobId);
};

/**
 * Returns true if a specific splat (or any splat) is currently active.
 */
window.isGaussianSplatActive = (jobId) =>
    jobId ? !!_activeSplats[jobId] : Object.keys(_activeSplats).length > 0;

// ── Convenience: poll until job is complete then auto-load ───────────────────

/**
 * Poll the backend until a job reaches "success", then automatically
 * call window.loadGaussianSplat().
 *
 * @param {string} jobId
 * @param {object} [options]   - same as window.loadGaussianSplat options
 * @param {number} [maxWaitMs] - max total wait time in ms (default 10 min)
 */
window.watchAndLoadGaussianSplat = async (jobId, options = {}, maxWaitMs = 600_000) => {
    const pollUrl    = `${getBaseUrl()}/gaussian-splatting/status/${jobId}`;
    const intervalMs = 3000;
    const maxAttempts = Math.ceil(maxWaitMs / intervalMs);

    console.log(`⏳ [GaussianSplat] Polling job ${jobId} (max ${maxWaitMs / 1000}s)…`);

    for (let i = 0; i < maxAttempts; i++) {
        await new Promise((r) => setTimeout(r, intervalMs));
        try {
            const res  = await fetch(pollUrl, { credentials: 'include' });
            const data = await res.json();

            if (data.status === 'success') {
                console.log('✅ [GaussianSplat] Job complete — loading splat');
                return window.loadGaussianSplat(jobId, options);
            }
            if (data.status === 'failed') {
                console.error('❌ [GaussianSplat] Job failed:', data.error);
                return false;
            }
            console.log(`🔄 [GaussianSplat] Job status: ${data.status} (attempt ${i + 1}/${maxAttempts})`);
        } catch (err) {
            console.warn('[GaussianSplat] Poll error:', err.message);
        }
    }

    console.error('❌ [GaussianSplat] Timed out waiting for job:', jobId);
    return false;
};
